import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Briefcase,
  CheckCircle2,
  DollarSign,
  Calendar,
  ArrowRight,
  Shield,
  Layers,
  Sparkles,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import type { Service, ServiceCategory } from '../../types/database';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function RequestServicePage() {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [services, setServices] = useState<Service[]>([]);
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [selectedServiceId, setSelectedServiceId] = useState<string>('');
  const [fullName, setFullName] = useState(profile?.full_name || '');
  const [email, setEmail] = useState(profile?.email || '');
  const [phone, setPhone] = useState(profile?.phone || '');
  const [organization, setOrganization] = useState(profile?.organization_name || '');
  const [timeline, setTimeline] = useState('Standard');
  const [estimatedBudget, setEstimatedBudget] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchServices();
  }, [searchParams]);

  useEffect(() => {
    if (profile) {
      setFullName(profile.full_name || '');
      setEmail(profile.email || '');
      setPhone(profile.phone || '');
      setOrganization(profile.organization_name || '');
    }
  }, [profile]);

  const fetchServices = async () => {
    try {
      const { data: srvs } = await supabase
        .from('services')
        .select('*')
        .eq('is_active', true)
        .order('display_order');
      if (srvs && srvs.length > 0) {
        setServices(srvs as Service[]);
        const paramId = searchParams.get('serviceId');
        if (paramId && srvs.some((s) => s.id === paramId)) {
          setSelectedServiceId(paramId);
        } else {
          setSelectedServiceId(srvs[0].id);
        }
      }

      const { data: cats } = await supabase.from('service_categories').select('*');
      if (cats) setCategories(cats as ServiceCategory[]);
    } catch (err) {
      console.error(err);
    }
  };

  const selectedService = services.find((s) => s.id === selectedServiceId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fullName || !email || !selectedServiceId || !description) return;

    setSubmitting(true);
    try {
      // 1. Create Lead in Supabase
      const { data: leadData, error: leadErr } = await supabase
        .from('leads')
        .insert({
          full_name: fullName,
          email,
          phone,
          organization,
          service_id: selectedServiceId,
          source: user ? 'Client Portal' : 'Website Form',
          status: 'New Lead',
          estimated_budget: estimatedBudget ? parseFloat(estimatedBudget) : selectedService?.base_price,
          timeline,
          notes: description,
        })
        .select()
        .single();

      if (leadErr) throw leadErr;

      setSuccess(true);
      setTimeout(() => {
        navigate('/portal');
      }, 2500);
    } catch (err) {
      console.error('Request service error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />

      <main className="flex-1 pt-32 pb-20 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-clairBlue-50 text-clairBlue-700 border border-clairBlue-200 rounded-full text-xs font-semibold uppercase tracking-wider mb-4">
              <Sparkles className="w-4 h-4" />
              <span>Commission High-Quality Digital Execution</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-black text-[#1A2E4E] mb-3">
              Request a Professional Service
            </h1>
            <p className="text-sm text-gray-500 max-w-lg mx-auto">
              Select your required digital solution, specify your timeline and project brief, and receive a formal itemized quotation within 24 hours.
            </p>
          </div>

          <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 sm:p-12">
            {success ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-12"
              >
                <div className="w-16 h-16 bg-emerald-50 text-emerald-600 rounded-full mx-auto flex items-center justify-center mb-4">
                  <CheckCircle2 className="w-10 h-10" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Service Request Received!</h3>
                <p className="text-sm text-gray-600 max-w-md mx-auto mb-6">
                  Our engineering and business development team has received your project brief. You will be notified with your official quotation and project workspace.
                </p>
                <Link
                  to="/portal"
                  className="px-6 py-3 bg-darkBlue-600 text-white rounded-xl text-xs font-bold"
                >
                  Return to Client Portal
                </Link>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* 1. Service Selection */}
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-gray-700 mb-2">
                    Select Digital Service Offering *
                  </label>
                  <select
                    value={selectedServiceId}
                    onChange={(e) => setSelectedServiceId(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-clairBlue-500 text-sm font-medium"
                  >
                    {services.map((srv) => (
                      <option key={srv.id} value={srv.id}>
                        {srv.title} — ({srv.pricing_model}: ${srv.base_price} {srv.currency})
                      </option>
                    ))}
                  </select>

                  {selectedService && (
                    <div className="mt-3 p-4 bg-clairBlue-50/50 rounded-2xl border border-clairBlue-100 text-xs">
                      <p className="text-gray-700 font-medium mb-2">{selectedService.description}</p>
                      {selectedService.features && selectedService.features.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {selectedService.features.map((feat, i) => (
                            <span
                              key={i}
                              className="px-2.5 py-1 bg-white rounded-lg border border-gray-200 text-gray-600 font-medium text-[11px]"
                            >
                              ✓ {feat}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* 2. Client Details */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-700 mb-1.5">
                      Your Full Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder="e.g. Sarah Namubiru"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-700 mb-1.5">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="name@company.com"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-700 mb-1.5">
                      Telephone / WhatsApp
                    </label>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="+256 700 000000"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-700 mb-1.5">
                      Organization / Business Name
                    </label>
                    <input
                      type="text"
                      value={organization}
                      onChange={(e) => setOrganization(e.target.value)}
                      placeholder="e.g. Apex Logistics Ltd"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm"
                    />
                  </div>
                </div>

                {/* 3. Timeline & Estimated Budget */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-700 mb-1.5">
                      Desired Timeline
                    </label>
                    <select
                      value={timeline}
                      onChange={(e) => setTimeline(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm"
                    >
                      <option value="Standard">Standard Delivery (3-4 Weeks)</option>
                      <option value="Express">Express Delivery (10-14 Days)</option>
                      <option value="Rush">Rush Delivery (5-7 Days)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-700 mb-1.5">
                      Target Budget (USD)
                    </label>
                    <input
                      type="number"
                      value={estimatedBudget}
                      onChange={(e) => setEstimatedBudget(e.target.value)}
                      placeholder={`e.g. ${selectedService?.base_price || 250}`}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm"
                    />
                  </div>
                </div>

                {/* 4. Detailed Scope */}
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-gray-700 mb-1.5">
                    Project Scope & Requirements Description *
                  </label>
                  <textarea
                    rows={4}
                    required
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Describe your current setup, goals, target audience, specific features, reference links..."
                    className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-clairBlue-500"
                  />
                </div>

                <div className="pt-4 border-t border-gray-100 flex items-center justify-between">
                  <Link to="/portal" className="text-xs text-gray-400 hover:text-gray-600 font-semibold">
                    ← Back to Portal
                  </Link>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-8 py-3.5 bg-gradient-to-r from-darkBlue-600 to-clairBlue-500 text-white rounded-xl font-bold text-sm shadow-md hover:shadow-lg transition-all flex items-center gap-2 disabled:opacity-50"
                  >
                    {submitting ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <span>Submit Project Brief</span>
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
